import { ReactElement, useCallback } from 'react';
import { AspectRatio } from 'react-aspect-ratio';

import { CSSObject, useTheme, useMediaQuery, Box } from '@mui/material';

import { AnimatePresence } from 'framer-motion';

import { LogoProps } from './types';

import { useBoolean } from '../../../../../../common/hooks';

const style: CSSObject = {
  width: '100%',
  height: '100%',
  position: 'relative',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center'
};

export default function Logo({ invert }: LogoProps): ReactElement {
  const theme = useTheme();
  const isXs = useMediaQuery(theme.breakpoints.down('sm'));
  const isSm = useMediaQuery(theme.breakpoints.down('md'));

  const [isFullLoaded, setIsFullLoaded] = useBoolean();
  const [isFullError, setIsFullError] = useBoolean(true);

  const [isThumbnailLoaded, setIsThumbnailLoaded] = useBoolean();
  const [isThumbnailError, setIsThumbnailError] = useBoolean();

  const handleThumbnailError = useCallback(() => {
    setIsThumbnailLoaded.off();
    setIsThumbnailError.on();
  }, []);

  const handleThumbnailLoad = useCallback(() => {
    setIsThumbnailLoaded.on();
    setIsThumbnailError.off();

    setIsFullError.off();
  }, []);

  const handleFullError = useCallback(() => {
    setIsFullLoaded.off();
    setIsFullError.on();
  }, []);

  const handleFullLoad = useCallback(() => {
    setIsFullLoaded.on();
    setIsFullError.off();
  }, []);

  return (
    <AnimatePresence exitBeforeEnter initial={false}>
      <AspectRatio
        ratio='6/2'
        style={{
          width: isXs ? '100px' : isSm ? '125px' : '150px'
        }}>
        <Box sx={{ ...style, position: 'relative' }}>
          {/* Thumbnail image */}
          {!isThumbnailError && !isFullLoaded && (
            <img
              alt='Amazing Gaming Logo thumbnail'
              onError={() => handleThumbnailError()}
              onLoad={() => handleThumbnailLoad()}
              src={`https://cdn.tain.com/amazing/logo/${
                invert ? 'main_dark' : 'main_white'
              }/horizontal/45.png`}
              style={{ ...style, objectFit: 'contain' }}
            />
          )}

          {/* Full size image */}
          {!isFullError && isThumbnailLoaded && (
            <img
              alt='Amazing Gaming Logo'
              onError={() => handleFullError()}
              onLoad={() => handleFullLoad()}
              src={`https://cdn.tain.com/amazing/logo/${
                invert ? 'main_dark' : 'main_white'
              }/horizontal/original.png`}
              style={{ ...style, objectFit: 'contain' }}
            />
          )}
        </Box>
      </AspectRatio>
    </AnimatePresence>
  );
}
